import React, { useState, useEffect, useCallback } from 'react';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import { specialistsApi } from '../../services/apiService';
import SpecialistDashboardLayout from '../../components/SpecialistDashboardLayout';

const SpecialistRFQPage = () => {
  const { specialist } = useSpecialistAuth();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRfq, setSelectedRfq] = useState(null);

  const fetchRfqs = useCallback(async () => {
    if (!specialist?.id) return;
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'All') params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const data = await specialistsApi.getRfqs(specialist.id, params);
      setRfqs(data.rfqs || []);
    } catch (err) {
      console.error('Error fetching specialist RFQs:', err);
      setRfqs([]);
    } finally {
      setLoading(false);
    }
  }, [specialist?.id, statusFilter, searchQuery]);

  useEffect(() => {
    fetchRfqs();
  }, [fetchRfqs]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return { bg: '#E8F5E9', color: '#2E7D32' };
      case 'replied': return { bg: '#E3F2FD', color: '#1565C0' };
      case 'closed': return { bg: '#F3E5F5', color: '#7B1FA2' };
      default: return { bg: '#F5F5F5', color: '#666' };
    }
  };

  return (
    <SpecialistDashboardLayout>
      <div className="specialist-rfq-content" style={{ padding: '0' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#1F396C', margin: '0 0 8px 0' }}>RFQ Manage</h1>
          <p style={{ fontSize: '14px', color: '#6D6D6D', margin: 0 }}>View and manage project-related Request for Quotations sent to you.</p>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input 
            type="text" 
            placeholder="Search RFQs..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: '10px 16px', border: '1px solid #ddd', borderRadius: '8px', width: '280px', fontSize: '14px' }}
          />
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '10px 16px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
          >
            <option value="All">All Status</option>
            <option value="new">New</option>
            <option value="replied">Replied</option>
            <option value="closed">Closed</option>
          </select>
          <div style={{ marginLeft: 'auto', fontSize: '14px', color: '#6D6D6D', display: 'flex', alignItems: 'center' }}>
            {!loading && <span>{rfqs.length} RFQ{rfqs.length !== 1 ? 's' : ''} found</span>}
          </div>
        </div>

        <div style={{ 
          background: '#fff', 
          borderRadius: '12px', 
          border: '1px solid #eee', 
          overflow: 'hidden' 
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '13px', color: '#6D6D6D', fontWeight: '600' }}>Date</th>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '13px', color: '#6D6D6D', fontWeight: '600' }}>Buyer Name</th>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '13px', color: '#6D6D6D', fontWeight: '600' }}>Category</th>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '13px', color: '#6D6D6D', fontWeight: '600' }}>Project</th>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '13px', color: '#6D6D6D', fontWeight: '600' }}>Location</th>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '13px', color: '#6D6D6D', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '14px 20px', textAlign: 'center', fontSize: '13px', color: '#6D6D6D', fontWeight: '600' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ padding: '60px 20px', textAlign: 'center', color: '#999', fontSize: '15px' }}>
                    Loading RFQs...
                  </td>
                </tr>
              ) : rfqs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '60px 20px', textAlign: 'center', color: '#999', fontSize: '15px' }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: '500', color: '#666' }}>No RFQs received yet</p>
                    <p style={{ margin: 0, fontSize: '13px' }}>New project-related RFQ requests will appear here</p>
                  </td>
                </tr>
              ) : rfqs.map((rfq) => {
                const statusStyle = getStatusColor(rfq.status);
                return (
                  <tr key={rfq.id} style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }} onClick={() => setSelectedRfq(selectedRfq?.id === rfq.id ? null : rfq)}>
                    <td style={{ padding: '14px 20px', fontSize: '14px', color: '#333' }}>{formatDate(rfq.createdAt)}</td>
                    <td style={{ padding: '14px 20px', fontSize: '14px', color: '#333', fontWeight: '500' }}>{rfq.fullName || rfq.companyName}</td>
                    <td style={{ padding: '14px 20px', fontSize: '14px', color: '#666' }}>{rfq.category}{rfq.subCategory ? ` / ${rfq.subCategory}` : ''}</td>
                    <td style={{ padding: '14px 20px', fontSize: '14px', color: '#666' }}>{rfq.projectTitle || '-'}</td>
                    <td style={{ padding: '14px 20px', fontSize: '14px', color: '#666' }}>{rfq.country}</td>
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{ 
                        padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                        background: statusStyle.bg, color: statusStyle.color, textTransform: 'capitalize'
                      }}>{rfq.status}</span>
                    </td>
                    <td style={{ padding: '14px 20px', textAlign: 'center' }}>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setSelectedRfq(selectedRfq?.id === rfq.id ? null : rfq); }}
                        style={{ background: 'none', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px', color: '#1F396C' }}
                      >View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* RFQ Detail Panel */}
        {selectedRfq && (
          <div style={{ 
            marginTop: '20px', background: '#fff', borderRadius: '12px', 
            border: '1px solid #eee', padding: '24px' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1F396C', fontSize: '18px' }}>RFQ Details</h3>
              <button 
                onClick={() => setSelectedRfq(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#999' }}
              >×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Buyer</p>
                <p style={{ margin: '0 0 12px', fontSize: '15px', color: '#333', fontWeight: '500' }}>{selectedRfq.fullName} ({selectedRfq.companyName})</p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Contact</p>
                <p style={{ margin: '0 0 12px', fontSize: '15px', color: '#333' }}>{selectedRfq.email} | {selectedRfq.phoneNumber}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Category</p>
                <p style={{ margin: '0 0 12px', fontSize: '15px', color: '#333' }}>{selectedRfq.category}{selectedRfq.subCategory ? ` → ${selectedRfq.subCategory}` : ''}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Country</p>
                <p style={{ margin: '0 0 12px', fontSize: '15px', color: '#333' }}>{selectedRfq.country}</p>
              </div>
              {selectedRfq.projectTitle && (
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Project Title</p>
                  <p style={{ margin: '0 0 12px', fontSize: '15px', color: '#333', fontWeight: '500' }}>{selectedRfq.projectTitle}</p>
                </div>
              )}
              {selectedRfq.projectBudget && (
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Budget</p>
                  <p style={{ margin: '0 0 12px', fontSize: '15px', color: '#333' }}>{selectedRfq.projectBudget}</p>
                </div>
              )}
              {selectedRfq.projectTimeline && (
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Timeline</p>
                  <p style={{ margin: '0 0 12px', fontSize: '15px', color: '#333' }}>{selectedRfq.projectTimeline}</p>
                </div>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Details</p>
                <p style={{ margin: '0', fontSize: '15px', color: '#333', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{selectedRfq.details}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </SpecialistDashboardLayout>
  );
};

export default SpecialistRFQPage;
